<template>
  <!-- По месяцам -->
  <v-container fluid>
    <!-- Фильтры -->
    <v-card>
      <v-card-title>
        По месяцам
      </v-card-title>

      <v-card-text>
        <v-form ref="form" lazy-validation>
          <v-row>

            <v-col cols="12" sm="6" md="3">
              <DateFromToSelector v-model="filters.period" />
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <Project-Selector v-model="filters.project" single />
            </v-col>

            <v-col cols="12" sm="6" md="3">
              <Queue-Selector v-model="filters.queues" :project-ids="filters.project" no-rules />
            </v-col>

            <v-col cols="12" sm="12" md="4" lg="12" align="right" class="mt-auto">
              <v-btn
                color="info"
                class="mr-1"
                :disabled="excelLoading"
                @click="downloadExcel"
              >
                <v-progress-circular
                  v-if="excelLoading"
                  indeterminate
                  color="success"
                />
                <span v-else>Excel</span>
              </v-btn>

              <v-btn color="success" @click="submitForm">Поиск</v-btn>
            </v-col>
          </v-row>
        </v-form>

      </v-card-text>
    </v-card>

    <!-- Таблица вход -->
    <v-card class="mt-5" :loading="reportLoading">
      <v-simple-table v-if="report.items.length" dense>
        <thead>
          <tr>
            <th></th>
            <th
              v-for="month in report.horizontalHeaders"
              :key="`header_${month.tooltip}`"
              class="text-center cursor-pointer"
              @click="prepareAcceptedGraph(month.tooltip)"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-on="on" v-bind="attrs">{{ month.title }}</span>
                </template>
                <span>{{ month.tooltip }}</span>
              </v-tooltip>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(header, headerIndex) in report.verticalHeaders" :key="`row_${headerIndex}`">
            <th class="first-latter-capitalize">
              {{ header.title }}
            </th>
            <td v-for="n in report.horizontalHeaders.length" :key="`col_${n}`" class="text-center">
              {{ report.items[n - 1][header.code] }}
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>

    <!-- Диаграммы -->
    <v-card v-if="chartLoading === false && reportLoading === false" class="mt-5 pa-2" :loading="reportLoading">
      <v-row>
        <v-col cols="12" md="6">
          <apexchart
            type="line"
            height="350"
            :options="charts.totalAcceptedOptions"
            :series="charts.totalAcceptedSeries"
          />
        </v-col>
        <v-col v-if="showAcceptedLimits" cols="12" md="6">
          <apexchart
            v-if="acceptedLimitedLoading === false"
            type="pie"
            height="350"
            :options="charts.acceptedLimitsOptions"
            :series="charts.acceptedLimitsSeries"
          />
          <div v-if="acceptedLimitedLoading" style="display: flex; height: 100%">
            <v-progress-circular
              indeterminate
              color="green"
              :size="55"
              class="ma-auto"
            />
          </div>
        </v-col>
      </v-row>
    </v-card>

    <!-- Данные по полям формы -->
    <v-card v-if="formFieldsData.length > 0" class="mt-5">
      <v-card-title>
        Данные по полям формы
      </v-card-title>
      
      <v-card-text>
        <v-expansion-panels>
          <v-expansion-panel
            v-for="(field, fieldIndex) in formFieldsData"
            :key="`field_${fieldIndex}`"
          >
            <v-expansion-panel-header>
              <div class="d-flex align-center justify-space-between w-100">
                <span>{{ field.field_title }}</span>
                <span class="text-caption grey--text">Всего: {{ field.total_count }}</span>
              </div>
            </v-expansion-panel-header>
            
            <v-expansion-panel-content>
              <v-data-table
                :headers="formFieldsHeaders"
                :items="field.data"
                :items-per-page="10"
                :page.sync="formFieldsPages[fieldIndex]"
                :options.sync="formFieldsTableOptions[fieldIndex]"
                class="elevation-1"
                dense
              >
                <template v-slot:item.value="{ item }">
                  <span class="text-truncate" style="max-width: 300px;" :title="item.value">
                    {{ item.value }}
                  </span>
                </template>
                
                <template v-slot:item.count="{ item }">
                  <v-chip
                    color="primary"
                    text-color="white"
                    small
                  >
                    {{ item.count }}
                  </v-chip>
                </template>
                
                <template v-slot:item.percent="{ item }">
                  <span class="font-weight-medium">{{ item.percent }}</span>
                </template>
              </v-data-table>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
    </v-card>

    <!-- Таблица  исход-->
    <v-card v-if="reportOutgoing.items.length" class="mt-5" :loading="loadingOutgoing">
      <v-card-title>Исходящие</v-card-title>
      <v-simple-table dense>
        <thead>
          <tr>
            <th></th>
            <th
              v-for="month in reportOutgoing.horizontalHeaders"
              :key="`header_${month.tooltip}`"
              class="text-center cursor-pointer"
            >
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <span v-on="on" v-bind="attrs">{{ month.title }}</span>
                </template>
                <span>{{ month.tooltip }}</span>
              </v-tooltip>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(header, headerIndex) in reportOutgoing.verticalHeaders" :key="`row_${headerIndex}`">
            <th class="first-latter-capitalize">
              {{ header.title }}
            </th>
            <td v-for="n in reportOutgoing.horizontalHeaders.length" :key="`col_${n}`" class="text-center">
              {{ reportOutgoing.items[n - 1][header.code] }}
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </v-card>
    
  </v-container>

</template>

<script>
import { mapActions } from 'vuex'
import DateFromToSelector from '~/components/partials/selectors/DateFromToSelector'
import ProjectSelector from '~/components/partials/selectors/ProjectSelector'
import QueueSelector from '~/components/partials/selectors/QueueSelector'
import { axios } from "~/plugins/axios"

export default {
  name: 'MonthlyReport',
  components: {
    ProjectSelector,
    QueueSelector,
    DateFromToSelector,
  },

  data() {
    return {
      filters: {
        period: [],
        project: [],
        queues: [],
      },
      excelLoading: false,

      reportLoading: false,
      chartLoading: true,
      acceptedLimitedLoading: false,

      rawReport: null,

      report: {
        horizontalHeaders: [],
        verticalHeaders: [],
        items: []
      },

      // Данные для полей формы
      formFieldsData: [],
      formFieldsPages: [],
      formFieldsTableOptions: [],
      formFieldsHeaders: [
        { text: 'Значение', value: 'value', sortable: false },
        { text: 'Количество', value: 'count', align: 'center', sortable: true },
        { text: 'Процент', value: 'percent', align: 'center', sortable: true }
      ],

      charts: {
        totalAcceptedOptions: {
          chart: {
            height: 350,
            type: 'line',
            toolbar: {
              show: true,
              tools: {
                download: true,
                selection: false,
                zoom: false,
                zoomin: false,
                zoomout: false,
                pan: false,
              }
            }
          },
          dataLabels: {
            enabled: false
          },
          stroke: {
            curve: 'straight'
          },
          title: {
            text: 'Динамики основных показателей входящих',
            align: 'left'
          },
          grid: {
            row: {
              colors: ['#e0e0e011', 'transparent'],
              opacity: 0.5
            },
          },
          xaxis: {
            categories: [],
          },
          theme: {
            mode: 'light'
          },
          colors: ['#4f81bd', '#c0504d'],
        },
        totalAcceptedSeries: [],

        acceptedLimitsOptions: {
          chart: {
            height: 350,
            type: 'pie',
            toolbar: {
              show: true,
              tools: {
                download: true,
                selection: false,
                zoom: false,
                zoomin: false,
                zoomout: false,
                pan: false,
              }
            }
          },
          title: {
            text: 'Общие сведения о поступивших',
            align: 'left'
          },
          legend: {
            position: 'bottom'
          },
          colors: [
            '#4f81bd',
            '#9bbb59',
            '#c0504d',
          ],
          labels: [
            'Принятые',
            'Пропущенные менее порогового значения',
            'Пропущенные более порогового значения',
          ],
          tooltip: {
            theme: 'dark',
            custom: this.tooltip,
          },
          theme: {
            mode: 'dark',
          }
        },
        acceptedLimitsSeries: [],
      },

      loadingOutgoing: false,
      reportOutgoing: {
        horizontalHeaders: [],
        verticalHeaders: [],
        items: []
      },
    }
  },

  computed: {
    isDarkTheme() {
      return this.$vuetify.theme.isDark;
    },

    showAcceptedLimits() {
      return this.charts?.acceptedLimitsSeries?.length > 0
    }
  },

  watch: {
    isDarkTheme() {
      this.changeApexTheme();
    },
  },

  methods: {
    ...mapActions({
      setError: 'shared/setError',
      setLoading: 'shared/setLoading',
    }),

    prepareReport(data) {
      const report = {}
      report.horizontalHeaders = []
      report.verticalHeaders = data.headers

      for (const item of data.items) {
        report.horizontalHeaders.push({
          title: item.translation_month,
          tooltip: item.month,
        })
      }
      report.items = data.items?.map(item => item.data)
      return report
    },

    prepareCharts(data) {
      this.chartLoading = true

      const series = [
        {name: 'Поступившие', data: []},
        {name: 'Принятые', data: []},
      ]
      const xaxis = [] 
      data?.items?.forEach(item => {
        xaxis.push(item.translation_month)
        series[0].data.push(item.data.total_calls_count)
        series[1].data.push(item.data.accepted_calls_count)
      })

      this.charts.totalAcceptedOptions.xaxis.categories = xaxis
      this.charts.totalAcceptedSeries = series
      this.chartLoading = false
    },

    tooltip({series, seriesIndex: index}) {
      const labels = [
        'Принятые',
        'Пропущенные менее порогового значения',
        'Пропущенные более порогового значения',
      ]
      const colorsForTooltip = this.charts.acceptedLimitsOptions.colors
      const label = labels[index]
      const color = colorsForTooltip[index]
      const val = series[index]
      return `<div class="apexcharts-tooltip-rangebar" style="background: ${color};color: white;">
          <span>
            ${label}: ${val}
          </span>
        </div>
      `
    },

    changeApexTheme() {
      if (this.isDarkTheme) {
        this.charts.totalAcceptedOptions = {
          ...this.charts.totalAcceptedOptions,
          chart: {
            ...this.charts.totalAcceptedOptions.chart,
            background: '#00000000',
            foreColor: '#f6f7f8'
          },
          theme: {
            mode: 'dark',
          },
        }
        this.charts.acceptedLimitsOptions = {
          ...this.charts.acceptedLimitsOptions,
          chart: {
            ...this.charts.acceptedLimitsOptions.chart,
            background: '#00000000',
            foreColor: '#f6f7f8'
          },
          theme: {
            mode: 'dark',
          },
        }
      } else {
        this.charts.totalAcceptedOptions = {
          ...this.charts.totalAcceptedOptions,
          chart: {
            ...this.charts.totalAcceptedOptions.chart,
            background: '#00000000',
            foreColor: '#373d3f'
          },
          theme: {
            mode: 'light',
          },
        }
        this.charts.acceptedLimitsOptions = {
          ...this.charts.acceptedLimitsOptions,
          chart: {
            ...this.charts.acceptedLimitsOptions.chart,
            background: '#00000000',
            foreColor: '#373d3f'
          },
          theme: {
            mode: 'light',
          },
        }
      }
    },

    prepareAcceptedGraph(month) {
      this.acceptedLimitedLoading = true
      if (month == null) {
        this.charts.acceptedLimitsSeries = []
        return 
      }
      const item = this.rawReport.items.find(item => item.month === month)

      this.charts.acceptedLimitsSeries = [
        item.data.accepted_calls_count,
        item.data.missed_calls_count_less_time,
        item.data.missed_calls_count_more_time
      ]

      this.charts.acceptedLimitsOptions.title.text = `Общие сведения о поступивших (${month} ${item.translation_month})`
      this.charts.acceptedLimitsOptions.labels = [
        'Принятые - ' + item.data.accepted_calls_count,
        'Пропущенные менее порогового значения - ' + item.data.missed_calls_count_less_time,
        'Пропущенные более порогового значения - ' + item.data.missed_calls_count_more_time
      ]

      setTimeout(() => {
        this.acceptedLimitedLoading = false
      }, 500);
    },

    async submitForm() {
      if (!this.$refs.form.validate()) return
      this.reportLoading = true
      this.loadingOutgoing = true
      this.setLoading(true)

      try {
        this.report.horizontalHeaders.length = 0
        this.report.verticalHeaders.length = 0
        this.report.items.length = 0

        const payload = {...this.filters}
        payload.project = payload.project[0]
        if (payload.queues?.length === 0) delete payload.queues
        if (payload.period.length < 2 || payload.period.includes(null)) delete payload.period

        const response = await axios.post('/api/reports/speech-analysis/annual-report-on-queues', payload)
        this.rawReport = response.data
        this.report = this.prepareReport(response?.data)
        this.prepareCharts(response?.data)
        this.prepareAcceptedGraph(response.data?.items[response.data.items.length - 1]?.month)

        // Загружаем данные полей формы
        await this.loadFormFieldsData()


        const responseOnPrefix = await axios.post('/api/reports/speech-analysis/annual-report-on-prefix', payload)
        this.reportOutgoing = this.prepareReport(responseOnPrefix.data)

      } catch (error) {
        let payload = {message: error}
        if (error?.response?.data) payload = error.response.data
        this.setError(payload)
        console.error(error)
      } finally {
        this.reportLoading = false
        this.loadingOutgoing = false
        this.setLoading(false)
        this.excelLoading = false
      }
    },

    async loadFormFieldsData() {
      try {
        // Создаем отдельный payload для API полей формы
        const formFieldsPayload = {...this.filters}
        formFieldsPayload.project = formFieldsPayload.project[0]
        if (formFieldsPayload.queues?.length === 0) delete formFieldsPayload.queues
        if (formFieldsPayload.period.length < 2 || formFieldsPayload.period.includes(null)) delete formFieldsPayload.period

        const response = await axios.post('/api/reports/form-field-monthly-report', formFieldsPayload)
        const list = Array.isArray(response.data) ? response.data : []
        this.formFieldsData = list.filter(item => Number(item?.total_count) > 0)
        // Инициализируем страницы для каждого поля
        this.formFieldsPages = new Array(this.formFieldsData.length).fill(1)
        this.formFieldsTableOptions = this.formFieldsData.map(() => ({
          page: 1,
          itemsPerPage: 10,
          sortBy: [],
          sortDesc: [],
        }))
      } catch (error) {
        console.error('Ошибка загрузки данных полей формы:', error)
        this.formFieldsData = []
      }
    },

    async downloadExcel() {
      this.setLoading(true)

      try {
        const payload = {...this.filters}
        payload.project = payload.project[0]
        if (payload.queues?.length === 0) delete payload.queues
        if (payload.period.length < 2 || payload.period.includes(null)) delete payload.period
  
        const headers = {
          responseType: "blob",
        }
        const response = await axios.post('/api/reports/speech-analysis/annual-report-in-excel', payload, headers)
        this.utils.downloadXlsx(response)
      } catch (error) {
        console.error(error)
      } finally {
        this.setLoading(false)
      }
    },
  },

  mounted() {
    this.changeApexTheme()
  },
}
</script>

<style scoped>
.first-latter-capitalize::first-letter {
  text-transform: uppercase;
}
</style>




